import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input } from '@/components/ui';
import { addOption, listAllOptions, updateOption } from '@/data/client';
import type { OptionKind } from '@/data/types';
import { FormError } from '@/components/crm/Modal';
import { StageName } from './PipelineSettings';

export function ListSettings() {
  return (
    <div className="crm-grid2 crm-grid2--top">
      <OptionList kind="lead_source" title="Lead sources" hint="For manually added leads (cold call, event …)." />
      <OptionList kind="lost_reason" title="Lost reasons" hint="Asked for whenever a deal is marked lost." />
    </div>
  );
}

function OptionList({ kind, title, hint }: { kind: OptionKind; title: string; hint: string }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ['options', kind, 'all'], queryFn: () => listAllOptions(kind) });
  const [label, setLabel] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['options', kind] });
  const update = useMutation({ mutationFn: (v: { id: string; label?: string; archived?: boolean }) => updateOption(v.id, v), onSuccess: refresh });
  const add = useMutation({ mutationFn: () => addOption(kind, label), onSuccess: () => { setLabel(''); refresh(); } });

  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>{title}</h3></div>
      <p className="crm-cell-sub" style={{ marginTop: -8, marginBottom: 12 }}>{hint} Archived items stay on old records but leave the dropdown.</p>
      <ul className="crm-stages">
        {data.map((o) => (
          <li key={o.id} className={o.archived ? 'is-archived' : ''}>
            <StageName id={o.id} name={o.label} onSave={(l) => update.mutate({ id: o.id, label: l })} />
            <Button variant="ghost" size="sm" onClick={() => update.mutate({ id: o.id, archived: !o.archived })}>{o.archived ? 'Restore' : 'Archive'}</Button>
          </li>
        ))}
      </ul>
      <form className="crm-addopt" style={{ marginTop: 14 }} onSubmit={(e) => { e.preventDefault(); if (label.trim()) add.mutate(); }}>
        <Input label="Add" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Button type="submit" size="sm" disabled={!label.trim() || add.isPending}>Add</Button>
      </form>
      <FormError error={update.error ?? add.error} />
    </Card>
  );
}
